import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useDispatch, useSelector } from 'react-redux'
import { GETThemeResponse } from '@cbgms/api'
import { IRootReducerState } from 'state/rootReducer'
import { AsyncAction } from '@cbgms/base/redux/async-action'
import * as types from '../state/types'

export const SHOP_CACHE_KEY = 'SHOP'
export const THEME_CACHE_KEY = 'THEME'

const SHOP_THEME_QUERY_KEY = [SHOP_CACHE_KEY, THEME_CACHE_KEY] as const

// Module-level promise to deduplicate concurrent fetches.
// When queryClient.clear() cancels the React Query entry but the HTTP request
// is still in-flight, the new query observer reuses the same promise instead
// of firing a second network call.
let pendingThemeRequest: Promise<GETThemeResponse['Data']> | null = null

/**
 * Shared hook for fetching the current shop theme with deduplication.
 *
 * Uses a static query key because the API resolves the shop server-side
 * from the auth context.  On shop change, ShopChangeRefreshProvider calls
 * queryClient.clear() which removes the cached entry and the surviving
 * observer triggers exactly one refetch — deduplicated at the network
 * level via a shared in-flight promise.
 */
export const useShopTheme = () => {
  const dispatch = useDispatch()
  const currentShopId = useSelector<IRootReducerState, string>(state => state.auth.currentShop)

  return useQuery(
    SHOP_THEME_QUERY_KEY,
    async () => {
      if (!pendingThemeRequest) {
        pendingThemeRequest = (async () => {
          try {
            const action = AsyncAction<GETThemeResponse>({
              type: [types.GET_SHOP_THEME, types.GET_SHOP_THEME_SUCCESS, types.GET_SHOP_THEME_FAILED],
              payload: {
                api: 'gms',
                request: {
                  method: 'GET',
                  url: '/modules/shops/theme'
                }
              }
            })

            const result = await dispatch(action)
            return result.Data
          } finally {
            pendingThemeRequest = null
          }
        })()
      }

      return pendingThemeRequest
    },
    {
      enabled: !!currentShopId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  )
}

export const useRefreshShopTheme = () => {
  const queryClient = useQueryClient()

  return () =>
    queryClient.invalidateQueries({
      queryKey: SHOP_THEME_QUERY_KEY
    })
}
